import { Flex, Box, Stack, Skeleton } from '@mantine/core';
import {
  IconArmchair2,
  IconBellRinging,
  IconChevronLeft,
  IconChevronRight,
  IconCodeAsterix,
  IconDoorEnter,
  IconKey,
  IconPhone,
  IconRelationOneToOne,
  IconUsers,
} from '@tabler/icons-react';
import MenuItem from 'components/MenuItem';
import useLang from 'lang';
import SimpleLayout from 'layouts/SimpleLayout';
import { Link } from 'react-router-dom';
import {
  DASHBOARD_PATH,
  ECV_PAGE_PATH,
  RFID_PAGE_PATH,
  PIN_CODE_PAGE_PATH,
  TEMPORARY_PIN_CODE_PAGE_PATH,
  USERS_PAGE_PATH,
  DOOR_BELL_PAGE_PATH,
  CALL_ORDER_PAGE_PATH,
} from 'routes/paths';
import theme from 'theme';
import H1SemiBold from 'theme/components/Typography/H1SemiBold';
import P2Medium from 'theme/components/Typography/P2Medium';
import { useApi } from 'api/api-context';
import { useEffect, useState } from 'react';
import { GetCurrentUserResponse } from 'api/actions/get-current-user/get-current-user-response';
import panic from 'panic';

/**
 * House page
 */
const House = () => {
  const { _t } = useLang();
  const { getAction, buildingId } = useApi();
  const [loading, setLoading] = useState(true);
  const [currentBuilding, setCurrentBuilding] = useState<NonNullable<GetCurrentUserResponse['buildings']>[0] | null>(
    null
  );
  const [isParent, setIsParent] = useState(false);

  /**
   * Fetch current building data from GetCurrentUser API
   */
  useEffect(() => {
    const getCurrentUser = getAction('GetCurrentUser');

    getCurrentUser({})
      .then((response) => {
        const buildings = response.buildings || [];
        const building = buildings.find((b) => b.id === buildingId);
        setCurrentBuilding(building || null);
        setIsParent(response.user?.parent === null || response.user?.parent === undefined);
      })
      .catch(panic)
      .finally(() => setLoading(false));
  }, [getAction, buildingId]);

  return (
    <>
      <SimpleLayout>
        <Box pb="20px">
          <Box p="1rem">
            <Flex direction="row" align="center">
              <Link
                to={DASHBOARD_PATH.original}
                style={{ textDecoration: 'none', display: 'flex', alignContent: 'center', marginBottom: '0.5rem' }}
              >
                <IconChevronLeft size="1.5rem" color={theme.colors?.gray?.[9]} />
                <P2Medium color={theme.colors?.gray?.[9]}>{_t('Back')}</P2Medium>
              </Link>
            </Flex>
            <Flex gap="0.5rem">
              <IconArmchair2 size="2rem" color={theme.colors?.gray?.[12]} />
              <H1SemiBold color={theme.colors?.gray?.[12]}>{_t('My household')}</H1SemiBold>
            </Flex>
          </Box>
          {loading && (
            <Stack px={16}>
              <Skeleton height={80} radius="md" />
              <Skeleton height={80} radius="md" />
              <Skeleton height={80} radius="md" />
              <Skeleton height={80} radius="md" />
            </Stack>
          )}
          {/*EČV */}
          {currentBuilding?.showRegistrationNumbers && (
            <MenuItem
              to={ECV_PAGE_PATH.original}
              icon={IconRelationOneToOne}
              title={_t('My registration numbers')}
              description={_t('List and management of registration numbers')}
              iconRight={IconChevronRight}
            />
          )}
          {/*Zoznam čipov */}
          {currentBuilding?.showChipsList && (
            <MenuItem
              to={RFID_PAGE_PATH.original}
              icon={IconKey}
              title={_t('List of chips')}
              description={_t('List and management of chips')}
              iconRight={IconChevronRight}
            />
          )}
          {/* PIN code */}
          {currentBuilding?.showPinCode && (
            <MenuItem
              to={PIN_CODE_PAGE_PATH.original}
              icon={IconCodeAsterix}
              title={_t('PIN code')}
              description={_t('View and manage your access PIN')}
              iconRight={IconChevronRight}
            />
          )}
          {currentBuilding?.showPinCode && (
            <MenuItem
              to={TEMPORARY_PIN_CODE_PAGE_PATH.original}
              icon={IconDoorEnter}
              title={_t('Temporary entries')}
              description={_t('Create up to 5 temporary 6-digit PIN codes with a fixed start and end time.')}
              iconRight={IconChevronRight}
            />
          )}
          {/*Používatelia v domácnosti */}
          {currentBuilding?.showHouseholdUsers && (
            <MenuItem
              to={USERS_PAGE_PATH.original}
              icon={IconUsers}
              title={_t('Household users')}
              description={_t('List and management of users')}
              iconRight={IconChevronRight}
            />
          )}
          {/*Zmena menovky na zvončeku */}
          {currentBuilding?.showDoorBell && isParent && (
            <MenuItem
              to={DOOR_BELL_PAGE_PATH.original}
              icon={IconBellRinging}
              title={_t('Name on the doorbell')}
              description={_t('Edit the displayed name')}
              iconRight={IconChevronRight}
            />
          )}
          {/*Poradie volaní */}
          {currentBuilding?.showCallOrder && (
            <MenuItem
              to={CALL_ORDER_PAGE_PATH.original}
              icon={IconPhone}
              title={_t('Call order')}
              description={_t('Set the order in which you are contacted')}
              iconRight={IconChevronRight}
            />
          )}
        </Box>
      </SimpleLayout>
    </>
  );
};

export default House;
